import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { SearchButton, SearchField, usePaletteShortcut } from "@/features/knowledge/command-palette"
import { cn } from "@/lib/utils"
import { projectsQuery } from "@/lib/workspace-api"
import { WORDS } from "@/model/glossary"
import { SearchIcon } from "./ui/icons"
import { Kbd } from "./ui/kbd"

/** A row of the workspace search: a project to open, or the Knowledge search for the words. */
export type WorkspaceSearchRow =
  | { readonly _tag: "project"; readonly id: string; readonly name: string }
  | { readonly _tag: "knowledge"; readonly q: string }

/**
 * The rows for a query (UX pass phase 2, P14): the projects whose name matches (all of them, by name, while the query
 * is empty), at most 8, then "Search Knowledge for q" once there are words. Archived projects only when they match.
 */
export const workspaceSearchRows = (
  q: string,
  projects: ReadonlyArray<{ readonly id: string; readonly name: string; readonly status: string }>,
): ReadonlyArray<WorkspaceSearchRow> => {
  const words = q.trim().toLowerCase()
  const shown = projects
    .filter((p) => (words ? p.name.toLowerCase().includes(words) : p.status !== "archived"))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .slice(0, 8)
    .map((p): WorkspaceSearchRow => ({ _tag: "project", id: p.id, name: p.name }))
  return words ? [...shown, { _tag: "knowledge", q: q.trim() }] : shown
}

/**
 * Search on the workspace pages (UX pass phase 2, P14: one header): the same field as inside a project ("Search
 * jung.dev…", ⌘K), opening a small palette that jumps to a project or searches Knowledge (people pages and
 * guidelines). Inside a project, ⌘K is the project's own palette.
 */
export function WorkspaceSearch({
  workspaceId,
  workspaceName,
}: {
  workspaceId: string
  workspaceName: string
}) {
  const [open, setOpen] = useState(false)
  usePaletteShortcut(useCallback(() => setOpen((v) => !v), []))
  return (
    <>
      <span className="max-md:hidden">
        <SearchField projectName={workspaceName} onOpen={() => setOpen(true)} />
      </span>
      <span className="md:hidden">
        <SearchButton onOpen={() => setOpen(true)} />
      </span>
      <WorkspacePalette
        open={open}
        onClose={() => setOpen(false)}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
      />
    </>
  )
}

function WorkspacePalette({
  open,
  onClose,
  workspaceId,
  workspaceName,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  workspaceName: string
}) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click; Escape closes the dialog natively
    <dialog
      ref={ref}
      aria-label="Search"
      onClose={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      className={cn(
        "m-0 max-h-none max-w-none bg-surface p-0 text-ink shadow-sheet",
        "mx-auto mt-[12dvh] flex max-h-[70dvh] w-[min(640px,calc(100vw-32px))] flex-col overflow-hidden rounded-card border border-rule",
        "max-sm:mt-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:rounded-none max-sm:border-0",
        !open && "hidden",
      )}
    >
      {open && <PaletteBody workspaceId={workspaceId} workspaceName={workspaceName} onClose={onClose} />}
    </dialog>
  )
}

function PaletteBody({
  workspaceId,
  workspaceName,
  onClose,
}: {
  workspaceId: string
  workspaceName: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const projects = useQuery(projectsQuery(workspaceId)).data ?? []
  const [q, setQ] = useState("")
  const [selected, setSelected] = useState(0)
  const rows = useMemo(() => workspaceSearchRows(q, projects), [q, projects])
  const listId = useId()
  const at = Math.min(selected, Math.max(0, rows.length - 1))
  const choose = (row: WorkspaceSearchRow) => {
    onClose()
    if (row._tag === "project")
      void navigate({ to: "/w/$workspaceId/p/$projectId", params: { workspaceId, projectId: row.id } })
    else void navigate({ to: "/w/$workspaceId/knowledge", params: { workspaceId }, search: { q: row.q } })
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-rule px-4">
        <SearchIcon size={16} className="text-muted" />
        <input
          // biome-ignore lint/a11y/noAutofocus: the palette exists to type into
          autoFocus
          type="text"
          role="combobox"
          aria-expanded={rows.length > 0}
          aria-controls={listId}
          aria-activedescendant={rows.length > 0 ? `${listId}-${at}` : undefined}
          aria-label={`Search ${workspaceName}`}
          placeholder={`Open a project, or search ${WORDS.knowledge}…`}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setSelected(0)
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault()
              const n = rows.length
              if (n > 0) setSelected((at + (e.key === "ArrowDown" ? 1 : n - 1)) % n)
            } else if (e.key === "Enter") {
              e.preventDefault()
              const row = rows[at]
              if (row) choose(row)
            }
          }}
          className="h-12 min-w-0 flex-1 rounded-control bg-transparent text-[15px] text-ink placeholder:text-placeholder focus-visible:outline-offset-[-3px] max-sm:text-[16px]"
        />
        <Kbd className="max-sm:hidden">esc</Kbd>
        <button
          type="button"
          onClick={onClose}
          className="tap -mr-2 min-h-11 shrink-0 rounded-control px-2 text-[15px] text-ink sm:hidden"
        >
          Cancel
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-5 text-[13px] text-muted">
          Type a project's name, or words to find in {WORDS.knowledge} (people pages and guidelines).
        </p>
      ) : (
        <div
          id={listId}
          role="listbox"
          aria-label="Results"
          className="min-h-0 flex-1 overflow-y-auto py-1.5"
        >
          {rows.map((row, i) => (
            <div
              key={row._tag === "project" ? row.id : "knowledge"}
              id={`${listId}-${i}`}
              role="option"
              tabIndex={-1}
              aria-selected={i === at}
              onMouseMove={() => setSelected(i)}
              onClick={() => choose(row)}
              onKeyDown={() => {}}
              className={cn(
                "flex min-h-11 cursor-pointer items-center gap-2.5 px-4 text-[13px] sm:min-h-9",
                i === at ? "bg-fill" : "hover:bg-fill-soft",
                row._tag === "knowledge" && i > 0 && "mt-1 border-t border-rule",
              )}
            >
              {row._tag === "project" ? (
                <>
                  <span className="mono-data w-[68px] shrink-0 text-[12px] text-muted">Project</span>
                  <span className="min-w-0 flex-1 truncate text-ink">{row.name}</span>
                </>
              ) : (
                <>
                  <SearchIcon className="text-muted" />
                  <span className="min-w-0 flex-1 truncate">
                    Search {WORDS.knowledge} for <span className="font-semibold">“{row.q}”</span>
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
